<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { addSizeToDataLayer } from "@/functions/analytics";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import { Props } from "@/components/ShapeType.vue";
import ValidationMessage from '@/components/UI/ValidationMessage.vue';

const store = useStore();
const { isCutout, action, prefix } = defineProps<Props>();

const dimensions = computed(() => store.getters.getMeasurements( 'barrel', isCutout ) );
const dimensionLabel = computed(() => store.getters.dimensionLabel);
const dimensionMaxLabel = computed(() => store.getters.getMaxLabelForDimension);
const isDimensionValid = computed(() => store.getters.isShapeValid( isCutout ) );
const message = computed(() => store.getters.getInstructionsForDimension);

const width = computed({
	get: () => dimensions.value.width,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "width",
			value: value,
		});
	},
});

const height = computed({
	get: () => dimensions.value.height,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "height",
			value: value,
		});
	},
});

const sideHeight = computed({
	get: () => dimensions.value.side_height,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "side_height",
			value: value,
		});
	},
});

</script>
<template>
	<shape-input-fields-wrapper>
		<dimension-input
			inputRef="barrel-width"
			:id="`${prefix}measurements-barrel-width`"
			:name="`${prefix}measurements[dimensions][width]`"
			unit="cm"
			:label="dimensionLabel('width')"
			:instructions="isCutout ? '' : dimensionMaxLabel('width')"
			:invalid="!isDimensionValid(`${prefix}barrel-width`)"
			:onBlur="() => addSizeToDataLayer('width', +width, 'barrel')"
			v-model="width"
		/>
		<dimension-input
			inputRef="barrel-height"
			:id="`${prefix}measurements-barrel-height`"
			:name="`${prefix}measurements[dimensions][height]`"
			unit="cm"
			:label="dimensionLabel('height')"
			:instructions="isCutout ? '': dimensionMaxLabel('height')"
			:invalid="!isDimensionValid(`${prefix}barrel-height`)"
			:onBlur="() => addSizeToDataLayer('height', +height, 'barrel')"
			v-model="height"
		/>
		<dimension-input
			inputRef="barrel-side_height"
			:id="`${prefix}measurements-barrel-side_height`"
			:name="`${prefix}measurements[dimensions][side_height]`"
			unit="cm"
			:label="dimensionLabel('side_height')"
			:instructions="isCutout ? '' : dimensionMaxLabel('side_height')"
			:invalid="!isDimensionValid(`${prefix}barrel-side_height`)"
			:onBlur="() => addSizeToDataLayer('side_height', +sideHeight, 'barrel')"
			v-model="sideHeight"
		/>
		<div class="full" v-if="message('height', 'side_height', isCutout)">
			<validation-message type="notice">
				{{ message('height', 'side_height', isCutout) }}
			</validation-message>
		</div>
		<div class="full" v-if="message('side_height', 'height', isCutout)">
			<validation-message type="notice">
				{{ message('side_height', 'height', isCutout) }}
			</validation-message>
		</div>
	</shape-input-fields-wrapper>
</template>
<style scoped lang="scss">
.full {
	grid-column: 1 / -1;
}
</style>
